import { useCallback, useState } from 'react'
import type { ButtonHTMLAttributes } from 'react'

export interface ComboBoxOption {
  value: string
  label: string
  description?: string
}

export interface UseComboBoxProps {
  /** Currently selected value */
  selectedValue?: string | null
  /** Callback when selection changes */
  onValueChange: (value: string | null) => void
  /** Placeholder text for trigger button */
  placeholderText?: string
  /** Search placeholder text */
  searchText?: string
  /** Whether the combobox is disabled */
  isDisabled?: boolean
  /** CSS classes for the trigger button */
  buttonClassName?: string
  /** Font size variant */
  fontSize?: 'xs' | 'sm' | 'base'
  /** Whether popover is controlled externally */
  popoverOpen?: boolean
  /** Callback when popover open state changes */
  onPopoverOpenChange?: (open: boolean) => void
  /** Additional props for trigger button */
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement> & {
    [key: `data-${string}`]: string | undefined
  }
  /** Keyboard shortcut hint */
  searchShortcutHint?: string | Array<string> | null
}

export interface UseComboBoxReturn {
  /** Current open state */
  isOpen: boolean
  /** Set open state */
  setIsOpen: (open: boolean) => void
  /** Handle selection change */
  handleValueChange: (value: string) => void
  /** Whether using controlled open state */
  isControlled: boolean
  /** Current selected value (normalized) */
  currentValue: string
}

/**
 * useComboBox - Shared state management for ComboBox components
 * 
 * Provides consistent state management patterns for all ComboBox variants.
 * Handles controlled/uncontrolled popover state and value normalization.
 * 
 * @param props - Configuration options
 * @returns State and handlers for ComboBox implementation
 * 
 * @example
 * ```tsx
 * const {
 *   isOpen,
 *   setIsOpen,
 *   handleValueChange,
 *   currentValue
 * } = useComboBox({
 *   selectedValue: selectedUserId,
 *   onValueChange: setSelectedUserId,
 *   placeholderText: 'Select user...'
 * })
 * ```
 */
export function useComboBox({
  selectedValue,
  onValueChange,
  popoverOpen,
  onPopoverOpenChange,
}: Pick<UseComboBoxProps, 'selectedValue' | 'onValueChange' | 'popoverOpen' | 'onPopoverOpenChange'>): UseComboBoxReturn {
  const [internalOpen, setInternalOpen] = useState(false)
  
  const isControlled = typeof popoverOpen === 'boolean'
  const isOpen = isControlled ? popoverOpen : internalOpen
  
  const setIsOpen = useCallback((open: boolean) => {
    if (!isControlled) {
      setInternalOpen(open)
    }
    onPopoverOpenChange?.(open)
  }, [isControlled, onPopoverOpenChange])

  const handleValueChange = useCallback((value: string) => {
    onValueChange(value || null)
    setIsOpen(false)
  }, [onValueChange, setIsOpen])

  // Normalize selected value - null becomes empty string for combobox
  const currentValue = selectedValue ?? ''

  return {
    isOpen,
    setIsOpen,
    handleValueChange,
    isControlled,
    currentValue,
  }
}

/**
 * useComboBoxOptions - Helper for transforming data into ComboBox options
 * 
 * @param items - Array of items to transform
 * @param getLabel - Function to extract label from item
 * @param getValue - Function to extract value from item
 * @param getDescription - Optional function to extract description
 * @returns Array of ComboBox options
 * 
 * @example
 * ```tsx
 * const projectOptions = useComboBoxOptions(
 *   projects,
 *   (p) => p.name,
 *   (p) => p.id,
 *   (p) => p.description
 * )
 * ```
 */
export function useComboBoxOptions<T>(
  items: Array<T>,
  getLabel: (item: T) => string,
  getValue: (item: T) => string,
  getDescription?: (item: T) => string | undefined
): Array<ComboBoxOption> {
  return items.map(item => ({
    value: getValue(item),
    label: getLabel(item),
    description: getDescription?.(item),
  }))
}